/**
 * Audio Utilities for Realtime Conversation
 *
 * Shared audio processing functions for ElevenLabs and Gemini Live API integrations
 * Implements Google-style gapless audio playback with queuing
 */
import i18n from 'i18next';
import { withTimeout } from './promiseUtils';

/**
 * How long to wait for a suspended context to resume before treating it as unusable.
 * Under the autoplay policy `resume()` returns a promise that simply never settles until
 * a user gesture arrives, so this must be bounded or the caller waits forever.
 */
const RESUME_TIMEOUT_MS = 3000;

/**
 * Thrown when an AudioContext exists but cannot be made to run. The state it was stuck
 * in is carried through, since 'suspended' (autoplay policy) and 'closed' (torn down by
 * a previous session) point at completely different bugs.
 */
export class AudioContextUnavailableError extends Error {
  constructor(state) {
    super(`AudioContext could not be started (state: ${state})`);
    this.name = 'AudioContextUnavailableError';
    this.state = state;
  }
}

/**
 * Create an audio context with the specified sample rate
 * @param {number} sampleRate - Sample rate in Hz (e.g., 16000, 24000)
 * @returns {AudioContext} Web Audio API context
 */
export function createAudioContext(sampleRate) {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    const message = i18n.t('browser_not_supported', { defaultValue: 'Your browser does not support audio features. Please try using Chrome, Safari, or Firefox.' });
    alert(message);
    throw new Error('AudioContext is not supported in this browser');
  }
  return new AudioContextClass({ sampleRate });
}

/**
 * Make sure a context is actually running before anything is hung off it.
 *
 * A context that comes up suspended looks completely healthy: nodes connect, no error is
 * thrown, `onaudioprocess` simply never fires. The microphone then captures nothing and
 * the agent hears silence until it hangs up — which is exactly how a learner sat through
 * a full session on 2026-08-13 without a single audio frame leaving their phone.
 * Playback fails the same silent way, since it hangs off the same context.
 *
 * @param {AudioContext} audioContext - The context to start.
 * @param {object} [options]
 * @param {number} [options.timeoutMs] - How long to wait for resume() before giving up.
 * @returns {Promise<AudioContext>} The running context.
 * @throws {AudioContextUnavailableError} If it cannot be brought to 'running'.
 */
export async function ensureAudioContextRunning(audioContext, { timeoutMs = RESUME_TIMEOUT_MS } = {}) {
  if (!audioContext) {
    throw new AudioContextUnavailableError('missing');
  }

  if (audioContext.state === 'running') {
    return audioContext;
  }

  // A closed context can never be resumed — resume() rejects, and reusing one is a bug
  // in the caller's lifecycle rather than something to retry.
  if (audioContext.state !== 'closed') {
    try {
      await withTimeout(audioContext.resume(), timeoutMs, 'audioContext.resume');
    } catch {
      // Blocked or timed out. The state check below is the authority either way.
    }
  }

  if (audioContext.state !== 'running') {
    throw new AudioContextUnavailableError(audioContext.state);
  }

  return audioContext;
}

/**
 * Decode base64 audio to PCM16 format
 * @param {string} base64Audio - Base64-encoded audio data
 * @returns {Int16Array} Decoded PCM16 audio samples
 */
export function decodeBase64ToPCM16(base64Audio) {
  const binaryString = atob(base64Audio);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

/**
 * Convert PCM16 samples to Float32 for Web Audio API
 * @param {Int16Array} int16Array - PCM16 audio samples
 * @returns {Float32Array} Normalized Float32 samples [-1.0, 1.0]
 */
export function pcm16ToFloat32(int16Array) {
  const float32Array = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    float32Array[i] = int16Array[i] / (int16Array[i] < 0 ? 0x8000 : 0x7FFF);
  }
  return float32Array;
}

/**
 * Decode base64 audio and convert to Float32 for playback
 * @param {string} base64Audio - Base64-encoded audio data
 * @returns {Float32Array} Audio samples ready for Web Audio API
 */
export function decodeAudioToFloat32(base64Audio) {
  const int16Array = decodeBase64ToPCM16(base64Audio);
  return pcm16ToFloat32(int16Array);
}

/**
 * Create an audio buffer from Float32 samples
 * @param {AudioContext} audioContext - Web Audio API context
 * @param {Float32Array} float32Array - Audio samples
 * @param {number} sampleRate - Sample rate in Hz
 * @param {number} numberOfChannels - Number of audio channels (1 for mono, 2 for stereo)
 * @returns {AudioBuffer} Web Audio API buffer ready for playback
 */
export function createAudioBuffer(audioContext, float32Array, sampleRate, numberOfChannels = 1) {
  const audioBuffer = audioContext.createBuffer(
    numberOfChannels,
    float32Array.length,
    sampleRate
  );
  audioBuffer.getChannelData(0).set(float32Array);
  return audioBuffer;
}

/**
 * Schedule and play audio with gapless queuing (Google-style)
 * @param {AudioContext} audioContext - Web Audio API context
 * @param {AudioBuffer} audioBuffer - Audio buffer to play
 * @param {Object} refs - Refs object containing nextStartTime and audioQueue
 * @param {Function} onEnded - Callback when audio chunk finishes playing
 * @returns {AudioBufferSourceNode} The audio source node
 */
export function scheduleAudioPlayback(audioContext, audioBuffer, refs, onEnded) {
  const { nextStartTimeRef, audioQueueRef } = refs;

  // Schedule audio to play after previous chunks
  nextStartTimeRef.current = Math.max(nextStartTimeRef.current, audioContext.currentTime);

  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(audioContext.destination);

  // Track audio source in queue
  source.addEventListener('ended', () => {
    audioQueueRef.current.delete(source);
    if (onEnded) {
      onEnded(audioQueueRef.current.size === 0);
    }
  });

  source.start(nextStartTimeRef.current);
  nextStartTimeRef.current += audioBuffer.duration;
  audioQueueRef.current.add(source);

  return source;
}

/**
 * Stop all queued audio and reset timing
 * @param {Object} refs - Refs object containing nextStartTime and audioQueue
 */
export function stopAllAudio(refs) {
  const { nextStartTimeRef, audioQueueRef } = refs;

  for (const source of audioQueueRef.current) {
    try {
      source.stop();
    } catch (e) {
      // Source may have already stopped
    }
  }
  audioQueueRef.current.clear();
  nextStartTimeRef.current = 0;
}

/**
 * Reset audio queue and timing for new session
 * @param {Object} refs - Refs object containing nextStartTime and audioQueue
 */
export function resetAudioQueue(refs) {
  const { nextStartTimeRef, audioQueueRef } = refs;

  audioQueueRef.current.clear();
  nextStartTimeRef.current = 0;
}

/**
 * Encode Float32 PCM audio to Int16 for transmission
 * @param {Float32Array} float32Array - Audio samples
 * @returns {Int16Array} PCM16 samples ready for encoding
 */
export function float32ToPCM16(float32Array) {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16Array;
}

/**
 * Encode PCM16 to base64 for WebSocket transmission
 * @param {Int16Array} pcm16 - PCM16 audio samples
 * @returns {string} Base64-encoded audio
 */
export function encodePCM16ToBase64(pcm16) {
  return btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));
}

/**
 * Process microphone audio chunk for transmission
 * @param {Float32Array} inputData - Audio from microphone (Float32)
 * @returns {string} Base64-encoded PCM16 audio ready for WebSocket
 */
export function processMicrophoneChunk(inputData) {
  const pcm16 = float32ToPCM16(inputData);
  return encodePCM16ToBase64(pcm16);
}
